import { HomeOutlined } from '@ant-design/icons';
import { matchResourceFromRoute, useBreadcrumb, useLink, useResourceParams } from '@refinedev/core';
import type { RefineBreadcrumbProps } from '@refinedev/ui-types';
import { Breadcrumb as AntdBreadcrumb, type BreadcrumbProps as AntdBreadcrumbProps } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissionContext } from '@/contexts/PermissionContext';
import { RESOURCES } from '@/Resources';

type CoreBreadcrumbProps = RefineBreadcrumbProps<AntdBreadcrumbProps>;

interface ProtectedBreadcrumbProps extends CoreBreadcrumbProps {
  // When user lacks permission for a breadcrumb link:
  // - 'text': show item but without href
  // - 'hide': remove the item from the breadcrumb
  unauthorizedMode?: 'text' | 'hide';
}

const ProtectedBreadcrumb = ({
  breadcrumbProps,
  showHome = true,
  hideIcons = false,
  meta,
  minItems = 2,
  unauthorizedMode = 'text',
}: ProtectedBreadcrumbProps) => {
  const { t } = useTranslation();
  const { breadcrumbs } = useBreadcrumb({ meta });
  const Link = useLink();
  const { resources, action } = useResourceParams();
  const { hasPermission } = usePermissionContext();
  const rootRouteResource = matchResourceFromRoute('/', resources);
  const breadCrumbItems = useMemo(
    () =>
      breadcrumbs.length < minItems
        ? []
        : (breadcrumbs
            .map(({ label, icon, href }) => {
              let isAllowed = true;
              const labelTranslated = t(label);

              if (href) {
                const matched = matchResourceFromRoute(href, resources);
                const resourceName = matched?.resource?.name as RESOURCES | undefined;

                // Only allow navigation if user has 'list' permission on the resource
                if (matched?.found && resourceName) {
                  isAllowed = hasPermission('list', resourceName);
                }
              }

              // If has href but is not allowed, follow the selected unauthorized behavior
              if (href && !isAllowed) {
                if (unauthorizedMode === 'hide') return null;

                return {
                  key: `breadcrumb-item-${label}`,
                  title: (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                      }}>
                      {!hideIcons && icon}
                      <span>{labelTranslated}</span>
                    </div>
                  ),
                };
              }

              return {
                key: `breadcrumb-item-${labelTranslated}`,
                title: (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                    }}>
                    {!hideIcons && icon}
                    {href ? (
                      <Link to={href}>{labelTranslated}</Link>
                    ) : (
                      <span>{labelTranslated}</span>
                    )}
                  </div>
                ),
              };
            })
            .filter(Boolean) as any[]),
    [Link, breadcrumbs, hasPermission, hideIcons, minItems, resources, unauthorizedMode, t],
  );
  const getBreadcrumbItems = () => {
    if (showHome && rootRouteResource.found) {
      return [
        {
          key: 'breadcrumb-item-home',
          title: <Link to="/">{rootRouteResource?.resource?.meta?.icon ?? <HomeOutlined />}</Link>,
        },
        ...breadCrumbItems,
      ];
    }

    return breadCrumbItems;
  };

  if (action == 'list' || breadcrumbs.length < minItems) return null;

  return <AntdBreadcrumb items={getBreadcrumbItems()} {...breadcrumbProps} />;
};

export default ProtectedBreadcrumb;
